<script setup lang="ts">
import { computed } from 'vue';
import type { DealDashboard } from '@Types/dashboard.types';

const props = defineProps<{
    deal: DealDashboard;
}>();

// Split "123 Oak Street, Austin, TX 78701" into street + rest.
const addressLines = computed<{ street: string | null; locality: string | null }>(() => {
    const raw = props.deal.propertyAddress;
    if (!raw) return { street: null, locality: null };
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length <= 1) return { street: parts[0] ?? null, locality: null };
    return { street: parts[0], locality: parts.slice(1).join(', ') };
});
</script>

<template>
    <div v-if="addressLines.street" class="deal-header">
        <div class="deal-header__content">
            <span class="deal-header__label">Property address</span>
            <div class="deal-header__address">
                <div class="deal-header__street">{{ addressLines.street }}</div>
                <div v-if="addressLines.locality" class="deal-header__locality">{{ addressLines.locality }}</div>
            </div>
        </div>
        <div class="deal-header__illustration" aria-hidden="true">
            <svg viewBox="0 0 80 74" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#deal-header-clip)">
                    <!-- Sky background (primary @ low opacity) -->
                    <rect class="ill-bg" width="80" height="74" rx="6"/>
                    <!-- Clouds -->
                    <path opacity="0.6" d="M-17.895 25.8592C-18.6143 26.176 34.0977 25.8592 34.0977 25.8592C34.0977 25.8592 31.1246 23.5227 24.5886 23.1701C24.5886 23.1701 23.9964 19.5846 19.5392 19.0378C15.082 18.491 -1.85247 24.0695 -5.12044 23.7109C-8.38842 23.3524 -7.1992 21.1951 -17.895 25.8592Z" fill="white"/>
                    <path d="M32.0083 8.54181C31.1925 8.84387 90.9718 8.54181 90.9718 8.54181C90.9718 8.54181 87.6002 6.31337 80.1879 5.97711C80.1879 5.97711 79.5163 2.5575 74.4615 2.03601C69.4067 1.51452 50.2017 6.83485 46.4956 6.49289C42.7895 6.15093 44.1382 4.09348 32.0083 8.54181Z" fill="white"/>
                    <!-- Trees (dark, stays static) -->
                    <path class="ill-dark" d="M60.9542 64.3671H70.6219C73.8963 54.9185 72.0279 47.8725 72.0279 47.8725C72.3327 49.4022 71.057 51.9003 71.057 51.9003C71.0969 51.3404 71.0948 50.7908 71.057 50.256C70.8895 47.9326 70.1685 45.6451 68.9238 43.4877C68.9238 43.4877 67.9444 40.9646 64.0689 42.6604C63.5607 42.8921 63.1229 43.1917 62.7806 43.542C60.2333 46.0519 60.3742 51.4947 60.3742 51.4947C59.8046 50.0752 59.5545 47.7946 59.5545 47.7946C58.718 53.0126 60.9542 64.3671 60.9542 64.3671Z"/>
                    <path class="ill-dark" d="M69.4974 65.0997H74.3002C74.3002 65.0997 75.2331 54.2783 75.1116 53.9729C74.9901 53.6676 74.7889 54.1154 74.7889 54.1154L74.441 54.9153L74.3002 55.2444V55.1736C74.2278 54.4483 73.5862 48.4165 71.7158 47.3946C70.143 46.5354 69.5034 51.5645 69.3164 53.7211C69.2641 54.2952 69.244 54.6665 69.244 54.6665C69.244 54.6665 68.9926 51.3119 69.2038 49.2662C69.1977 49.2815 67.2971 57.5542 69.4974 65.0997Z"/>
                    <path class="ill-dark" d="M9.74547 63.987H16.1671C16.1671 63.987 17.7511 57.4156 17.0894 53.2684L16.8306 53.6895L16.3675 54.4393V54.449L16.1729 54.7575C16.1729 54.7575 16.1729 54.7342 16.1729 54.6906C16.0776 54.0115 17.7514 46.3315 12.7228 47.3984C10.3934 48.1781 9.7805 51.3079 9.51585 53.3295C9.44775 53.8689 9.4205 54.2162 9.4205 54.2162C9.4205 54.2162 6.80711 56.9131 9.74547 63.987Z"/>
                    <path class="ill-dark" d="M9.21352 65.0573H5.20003C5.20003 65.0573 3.55271 62.0633 3.96622 58.4691C3.96622 58.4691 3.96622 57.2686 4.52256 56.7122C5.0789 56.1559 5.19638 57.0584 5.19638 57.0584C5.19638 57.0584 5.19638 57.0382 5.19638 57.0004C5.25597 56.4118 5.19638 50.0362 7.35272 50.6804C8.80858 51.3562 9.19163 54.0687 9.35703 55.8208C9.3996 56.2882 9.41663 56.5892 9.41663 56.5892C9.41663 56.5892 11.05 58.9265 9.21352 65.0573Z"/>
                    <!-- House front face -->
                    <path d="M44.6373 63.9863H15.2436L15.2188 35.8876L29.8774 17.2539L44.4555 35.0246L44.534 35.1188L44.6373 63.9863Z" fill="white"/>
                    <!-- Bushes (dark) -->
                    <path class="ill-dark" d="M18.1072 61.1674H18.1148C18.9788 61.5736 19.5018 62.3352 19.4762 63.1491V65.0991H6.21476C6.14703 65.0528 6.05219 64.8883 6.21476 64.6009C6.67821 63.7204 7.32326 62.9613 8.10217 62.3796L8.1187 62.3669C8.21488 62.2923 8.31105 62.2225 8.40422 62.1559L8.46883 62.1114C8.73078 61.9308 9.00165 61.765 9.2803 61.6148C9.36746 61.5672 9.46814 61.6561 9.40352 61.7259C9.13031 62.019 8.8791 62.3341 8.65217 62.6684C8.63514 62.6906 8.62011 62.7128 8.60709 62.735C8.60737 62.7387 8.60737 62.7424 8.60709 62.7461C8.77921 62.5099 8.98149 62.3001 9.20817 62.1225H9.21568C10.0422 61.4498 11.2759 60.8913 11.8139 61.0024C12.5217 61.1467 12.7035 62.4304 12.7035 62.4304C12.7432 62.1389 12.8071 61.8517 12.8943 61.572C12.8356 61.9509 12.8302 62.3369 12.8783 62.7175C12.8786 62.7161 12.8789 62.7145 12.8793 62.7128C12.8853 62.6858 12.8883 62.6795 12.8988 62.6366C12.9094 62.5938 12.9244 62.5367 12.9454 62.4684C12.9574 62.4272 12.968 62.3939 12.9845 62.3478C13.001 62.3018 13.0176 62.2542 13.0371 62.2035L13.0551 62.159C13.2297 61.6865 13.5239 61.2738 13.9057 60.9659C14.1922 60.7582 14.5319 60.6476 14.8794 60.6485C15.8503 60.6433 16.8101 60.8661 17.6895 61.3006C17.2898 60.9151 16.5745 60.7691 16.5745 60.7691C17.1036 60.8054 17.6226 60.9402 18.1072 61.1674Z"/>
                    <!-- Door frame (dark) -->
                    <path class="ill-dark" d="M33.998 45H23.998V64H33.998V45Z"/>
                    <!-- Door (primary-light) -->
                    <path class="ill-light" d="M33.998 46H24.998V65H33.998V46Z"/>
                    <!-- Doorknob (dark) -->
                    <path class="ill-dark" d="M32.95 55.5137C33.2353 55.5137 33.4665 55.2986 33.4665 55.0332C33.4665 54.7679 33.2353 54.5527 32.95 54.5527C32.6648 54.5527 32.4336 54.7679 32.4336 55.0332C32.4336 55.2986 32.6648 55.5137 32.95 55.5137Z"/>
                    <!-- Roof (dark) -->
                    <path class="ill-dark" d="M66.0981 35.1346L44.3477 35.0481L29.7695 17.2773H53.9246L66.0981 35.1346Z"/>
                    <!-- House side wall (primary) -->
                    <path class="ill-primary" d="M66.1043 35.1411V64.1477L44.5134 63.986L44.4262 35.1411L44.3477 35.0469L66.0981 35.1334L66.1043 35.1411Z"/>
                    <!-- Windows (primary-light) -->
                    <path class="ill-light" d="M62.4919 52.7188H60.6348V57.3296H62.4919V52.7188Z"/>
                    <path class="ill-light" d="M56.0876 52.7188H54.2305V57.3296H56.0876V52.7188Z"/>
                    <path class="ill-light" d="M49.4821 52.7188H47.625V57.3296H49.4821V52.7188Z"/>
                    <path class="ill-light" d="M62.4919 43.3066H60.6348V47.9175H62.4919V43.3066Z"/>
                    <path class="ill-light" d="M56.0876 43.3066H54.2305V47.9175H56.0876V43.3066Z"/>
                    <path class="ill-light" d="M49.4821 43.3066H47.625V47.9175H49.4821V43.3066Z"/>
                    <!-- Round window frame (dark) -->
                    <path class="ill-dark" d="M32.7607 31.0121C32.7609 31.5691 32.5753 32.1125 32.2296 32.5672C31.8838 33.0219 31.3949 33.3655 30.8303 33.5507C30.2657 33.7359 29.6532 33.7536 29.0774 33.6012C28.5016 33.4488 27.9908 33.1339 27.6155 32.7C27.2402 32.266 27.0188 31.7344 26.982 31.1784C26.9452 30.6225 27.0948 30.0696 27.41 29.5961C27.7253 29.1226 28.1907 28.7518 28.7421 28.5348C29.2935 28.3178 29.9036 28.2654 30.4884 28.3848C31.1323 28.5162 31.7086 28.8483 32.1218 29.3262C32.5351 29.804 32.7605 30.3988 32.7607 31.0121Z"/>
                    <!-- Round window glass -->
                    <path d="M32.5318 31.2426C32.4379 31.6413 32.2247 32.0072 31.9169 32.298C31.6091 32.5888 31.2191 32.7927 30.7921 32.8861C30.365 32.9795 29.9183 32.9587 29.5034 32.826C29.0886 32.6933 28.7225 32.4541 28.4475 32.1361C28.1724 31.8181 27.9997 31.4343 27.9491 31.0288C27.8985 30.6234 27.9722 30.2129 28.1617 29.8448C28.3512 29.4766 28.6486 29.1658 29.0198 28.9483C29.391 28.7307 29.8208 28.6153 30.2595 28.6152C30.9033 28.7467 31.4796 29.0788 31.8929 29.5566C32.3062 30.0344 32.5316 30.6293 32.5318 31.2426Z" fill="white"/>
                    <!-- Right bushes (dark) -->
                    <path class="ill-dark" d="M70.9766 64.0988H47.4062L47.2967 62.7919C47.2492 62.2153 48.154 61.6694 49.7323 61.3811L49.7818 61.3715C50.7011 61.2083 51.633 61.1151 52.5685 61.0928C52.5685 61.0928 51.2692 61.1947 50.542 61.4638C52.2253 61.1514 53.9384 60.9994 55.6548 61.0102C56.2528 61.0051 56.8485 61.0809 57.4231 61.2351C58.0061 61.3852 58.538 61.6717 58.9683 62.0673L59.0034 62.098L59.0984 62.1999C59.1232 62.2268 59.146 62.2556 59.1687 62.2845C59.2001 62.3219 59.229 62.3611 59.2554 62.4017C59.2678 62.419 59.2782 62.4363 59.2885 62.4536C59.3771 62.1897 59.3662 61.9056 59.2575 61.6483C59.4078 61.8294 59.5257 62.0319 59.6066 62.2479C59.6066 62.2479 59.9371 61.3504 61.2241 61.2485C62.2012 61.1716 64.4446 61.5618 65.9485 62.0308H65.9589H65.9733C66.351 62.1411 66.7157 62.2867 67.062 62.4651H67.0475C67.0218 62.4481 66.9949 62.4326 66.967 62.419C66.3339 62.0586 65.6641 61.7573 64.9673 61.5195C64.9673 61.5195 66.0002 61.6848 67.3119 62.0308L67.4276 62.0615C67.5991 62.1076 67.7726 62.1576 67.9482 62.2095L67.9812 62.2191C69.1711 62.5728 70.3362 63.3589 71.2018 64.0181"/>
                    <!-- Grass strip (primary) -->
                    <rect class="ill-primary" x="-9.09766" y="64" width="107" height="10"/>
                </g>
                <defs>
                    <clipPath id="deal-header-clip">
                        <rect width="80" height="74" rx="6" fill="white"/>
                    </clipPath>
                </defs>
            </svg>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;
@use 'sass:color';

.deal-header {
    display: flex;
    gap: 16px;
    align-items: stretch;
    border: 1px solid $portal-border;
    border-radius: $portal-card-radius;
    padding: $portal-card-padding;
    background: $portal-surface;
    box-shadow: $portal-card-shadow;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:hover {
        border-color: color.adjust($dark-gray, $lightness: -15%);
    }

    &__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__label {
        font-size: $text-portal-small;
        font-weight: 700;
        color: $portal-text-mid;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &__address {
        display: flex;
        flex-direction: column;
        gap: 2px;
        line-height: 1.4;
    }

    &__street {
        font-size: $text-portal-body-lg;
        font-weight: 600;
        color: $portal-text;
    }

    &__locality {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
    }

    &__illustration {
        width: 80px;
        flex-shrink: 0;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        overflow: hidden;
        border-radius: 6px;

        svg {
            width: 100%;
            height: auto;
        }

        // Theme-aware illustration colors
        .ill-bg {
            fill: var(--ch-primary, $blue);
            fill-opacity: 0.15;
        }

        .ill-primary {
            fill: var(--ch-primary, $blue);
        }

        .ill-light {
            fill: var(--ch-primary-light, $light-blue);
        }

        .ill-dark {
            fill: $portal-text;
        }
    }
}
</style>
